// Static filter definitions (always available)
// These provide default options and can be enhanced by facets data

// Converts a Title Cased string (e.g. "Allergy And Immunology") to sentence case
// (e.g. "Allergy and immunology") for display purposes only — capitalizes just the
// first character and lowercases the rest.
const toSentenceCase = (str) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const STATIC_FILTER_DEFINITIONS = {
  // Main filters (shown inline)
  distance: {
    key: 'distance',
    label: 'Distance',
    options: [
      { value: '5', label: 'Within 5 miles' },
      { value: '25', label: 'Within 25 miles' },
      { value: '50', label: 'Within 50 miles' },
      { value: '100', label: 'Within 100 miles' },
      { value: '150', label: 'Within 150 miles' },
      { value: '200', label: 'Within 200 miles' },
      { value: 'custom', label: 'Custom distance', isCustom: true }
    ]
  },
  newPatientStatus: {
    key: 'newPatientStatus',
    label: 'New patient status',
    options: [
      { value: 'no-preference', label: 'No preference' },
      { value: 'accepting', label: 'Accepting new patients' }
    ]
  },
  pubTrans: {
    key: 'pubTrans',
    label: 'Public transportation',
    options: [
      { value: 'yes', label: 'Easy access to public transportation' }
    ]
  },
  visitType: {
    key: 'visitType',
    label: 'Visit type',
    options: [
      { value: 'no-preference', label: 'No preference' },
      { value: 'virtual-care', label: 'Virtual care', hasInfoIcon: true, infoHeading: 'Virtual care', infoBody: 'Telehealth or telemedicine. Use a phone or computer to connect with your provider. This works when you don\'t need to see them in person.' }
    ]
  },
  gender: {
    key: 'gender',
    label: 'Provider gender',
    options: [
      { value: 'no-preference', label: 'No preference' },
      { value: 'female', label: 'Female' },
      { value: 'male', label: 'Male' }
    ]
  },
  // Sidebar filters
  languagesSpoken: {
    key: 'language',
    label: 'Languages spoken',
    options: [] // Will be populated by facets
  },
  specialties: {
    key: 'specialty',
    label: 'Specialties',
    options: [] // Will be populated by facets
  },
  boardCertification: {
    key: 'boardCertified',
    label: 'Board certification',
    hasInfoIcon: true,
    infoHeading: 'Board certification',
    infoBody: '<span>This provider passed special tests in their field of health care. You can find out if they are <a href="https://www.certificationmatters.org/find-my-doctor/" target="_blank" rel="noopener noreferrer">board-certified</a> in a specialty. Learn more about <a href="https://www.certificationmatters.org/" target="_blank" rel="noopener noreferrer">board certification</a>.</span>',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' }
    ]
  },
  agesServed: {
    key: 'agesServed',
    label: 'Ages served',
    options: [
      { value: 'no-preference', label: 'No preference' },
      { value: 'birth-to-17', label: 'Birth to age 17' },
      { value: '18-plus', label: 'Ages 18+' }
    ]
  },
  birthingFriendlyCare: {
    key: 'birthingFriendlyCare',
    label: 'Birthing-friendly care',
    hasInfoIcon: true,
    infoHeading: 'Birthing-friendly care',
    infoBody: 'This is a hospital or health system that tries to improve care before and after birth. They use safety methods that work. And they focus on health equity. The goal is to care for people from all racial and ethnic groups.',
    options: [
      { value: 'no-preference', label: 'No preference' },
      { value: 'yes', label: 'Birthing-friendly care' }
    ]
  },
  groupAffiliations: {
    key: 'groupAffiliation',
    label: 'Group affiliations',
    hasInfoIcon: true,
    infoHeading: 'Group affiliations',
    infoBody: 'Shows what practice or group a provider takes part in. They can provide care as part of the group. They also share practice resources.',
    options: [] // Will be populated by facets
  },
  handicapAccessibility: {
    key: 'handicapAccess',
    label: 'Handicap accessibility',
    options: [] // Will be populated by facets
  },
  specialProviderType: {
    key: 'specialProviderType',
    label: 'Special provider type',
    options: [] // Will be populated by facets
  },
  hospitalAffiliations: {
    key: 'hospitalAffiliation',
    label: 'Hospital affiliations',
    hasInfoIcon: true,
    infoHeading: 'Hospital affiliations',
    infoBody: 'Shows what hospital network a provider or group takes part in. They provide care as part of the network. They also share network resources.',
    options: [] // Will be populated by facets
  },
  specializedTraining: {
    key: 'specializedTraining',
    label: 'Special skills',
    options: [] // Will be populated by facets
  },
  accreditation: {
    key: 'accreditation',
    label: 'Accreditation',
    hasInfoIcon: true,
    infoHeading: 'Accreditation',
    infoBody: '<span>Expert health groups have checked this location. They say it meets quality standards for patient care and safety. You can find out if certain groups <a href="https://www.jointcommission.org/en/about-us/recognizing-excellence/find-accredited-international-organizations" target="_blank" rel="noopener noreferrer">accredit a location</a>.</span>',
    options: [] // Will be populated by facets
  },
  afterHoursWeekend: {
    key: 'afterHoursWeekend',
    label: 'Weekend or after-hours appointments',
    options: [
      { value: 'weekend appointments', label: 'Weekend appointments' },
      { value: 'after-hours appointments', label: 'After-hours appointments' }
    ]
  },
  providerRace: {
    key: 'providerRace',
    label: 'Provider race',
    options: [] // Will be populated by facets
  },
  providerEthnicity: {
    key: 'providerEthnicity',
    label: 'Provider race/ethnicity',
    options: [] // Will be populated by facets
  },
  cshcs: {
    key: 'cshcs',
    label: "Children's special health care services (CSHCS)",
    options: [
      { value: 'yes', label: 'Provider offers CSHCS' }
    ]
  }
};

// Map feature flag keys to filter definitions
const FEATURE_FLAG_TO_FILTER_KEY = {
  distance: 'distance',
  newPatientStatus: 'newPatientStatus',
  pubTrans: 'pubTrans',
  visitType: 'visitType',
  gender: 'gender',
  languagesSpoken: 'languagesSpoken',
  specialties: 'specialties',
  boardCertification: 'boardCertification',
  agesServed: 'agesServed',
  birthFriendly: 'birthingFriendlyCare',
  groupAffiliations: 'groupAffiliations',
  handicapAccessibility: 'handicapAccessibility',
  hospitalAffiliations: 'hospitalAffiliations',
  specializedTraining: 'specializedTraining',
  accreditation: 'accreditation',
  afterHoursWeekend: 'afterHoursWeekend',
  race: 'providerRace',  // Feature flag is 'race', maps to providerRace filter
  ethnicity: 'providerEthnicity',  // Feature flag is 'ethnicity', maps to providerEthnicity filter
  cshcsProvider: 'cshcs',
  specialProviderType: 'specialProviderType'
};

// Map facet keys to filter keys
const FACET_KEY_TO_FILTER_KEY = {
  languagesSpoken: 'languagesSpoken',
  specialties: 'specialties',
  groupAffiliations: 'groupAffiliations',
  hospitalAffiliations: 'hospitalAffiliations',
  handicapAccessibility: 'handicapAccessibility',
  specializedTraining: 'specializedTraining', // Map specialSkills to specializedTraining
  race: 'providerRace',  // Use race facet for provider race filter
  ethnicity: 'providerEthnicity',  // Use ethnicity facet for provider race/ethnicity filter
  accreditation: 'accreditation', // Map accreditation facet (accreditingOrgName) to accreditation filter
  specialProviderType: 'specialProviderType',
  // Previously these "main" (inline, single-select) filters always rendered their full
  // hardcoded STATIC_FILTER_DEFINITIONS option list regardless of what the current search
  // results actually contained — e.g. showing both "Female"/"Male" even when every result
  // was male, or "Accepting new patients" even when zero results in the current facets
  // response support it. Per the "move all filter render/choices to facets" request, these
  // now also pull from the facets response — see FIXED_OPTION_FACET_FILTER_KEYS below for
  // how they're handled differently from the raw-value sidebar filters above (their
  // options are a small fixed vocabulary with app-specific labels, not an open-ended list
  // of raw facet strings to display as-is).
  visitType: 'visitType',
  agesServed: 'agesServed',
  pubTrans: 'pubTrans',
  gender: 'gender',
  newPatientStatus: 'newPatientStatus',
  boardCertification: 'boardCertification'
};

// Filter keys whose STATIC_FILTER_DEFINITIONS options are a small, fixed, app-labeled
// vocabulary (e.g. "No preference" / "Female" / "Male") rather than an open-ended list of
// raw facet strings to render as-is (like specialties or group affiliations). For these,
// buildDynamicFilters below keeps the static option list's friendly labels/values and any
// always-available sentinel ("No preference") option, but narrows the rest down to only
// the choices the facets response actually has data for, and attaches each option's
// result count — rather than replacing the option list wholesale with raw facet values
// (which would lose the friendly labels and drop the "No preference" sentinel entirely).
const FIXED_OPTION_FACET_FILTER_KEYS = new Set([
  'visitType', 'agesServed', 'pubTrans', 'gender', 'newPatientStatus', 'boardCertification'
]);

// Sentinel option values that should always remain visible for a fixed-option filter
// regardless of what the facets response contains — these represent "don't filter"/
// no-op choices, not actual data values a facet could ever report.
const ALWAYS_VISIBLE_OPTION_VALUES = new Set(['no-preference']);

// Translates a facet's raw value text into the corresponding STATIC_FILTER_DEFINITIONS
// option `value` for FIXED_OPTION_FACET_FILTER_KEYS filters, since the facets API's raw
// value strings ("virtual care offered", "board certified", "accepting new patients")
// don't match this app's existing option value slugs ("virtual-care", "yes", "accepting").
// Filters not listed here (gender, agesServed, pubTrans) already use facet value text that
// matches their static option values exactly (case-insensitively), so no translation entry
// is needed for them.
const FIXED_OPTION_VALUE_ALIASES = {
  visitType: { 'virtual care offered': 'virtual-care' },
  boardCertification: { 'board certified': 'yes' },
  newPatientStatus: { 'accepting new patients': 'accepting' }
};

/**
 * Build dynamic filter list based on feature flags and facets
 * @param {Object} featureFlags - Feature flags from plan features (filters section)
 * @param {Object} facets - Facets data from facets API (parsed)
 * @returns {Array} Array of filter objects with options
 */
export const buildDynamicFilters = (featureFlags, facets) => {
  const filters = [];
  
  // If no feature flags, return empty array
  if (!featureFlags || !featureFlags.filters) {
    console.warn('No feature flags provided, returning empty filter list');
    return filters;
  }
  
  // Define the order of filters (first 4 are main filters, rest are sidebar)
  const filterOrder = [
    'distance',
    'newPatientStatus',
    'visitType',
    'gender',
    'ethnicity',
    'agesServed',
    'languagesSpoken',
    'handicapAccessibility',
    'birthFriendly',
    'specialties',
    'specializedTraining',
    'cshcsProvider',
    'specialProviderType',
    'pubTrans',
    'afterHoursWeekend',
    'boardCertification',
    'accreditation',
    'groupAffiliations',
    'hospitalAffiliations',    
    'race'      
  ];
  
  filterOrder.forEach(featureFlagKey => {
    // Check if this filter is enabled in feature flags
    const isEnabled = featureFlags.filters[featureFlagKey]?.toUpperCase() === 'Y';
    
    if (!isEnabled) {
      return; // Skip disabled filters
    }
    
    // Get the filter definition
    const filterKey = FEATURE_FLAG_TO_FILTER_KEY[featureFlagKey];
    const filterDef = STATIC_FILTER_DEFINITIONS[filterKey];
    
    if (!filterDef) {
      console.warn(`No filter definition found for feature flag: ${featureFlagKey}`);
      return;
    }
    
    // Clone the filter definition
    const filter = { ...filterDef };
    
    // Try to populate options from facets if available
    if (facets) {
      const facetKey = Object.keys(FACET_KEY_TO_FILTER_KEY).find(
        key => FACET_KEY_TO_FILTER_KEY[key] === filterKey
      );

      if (facetKey && facets[facetKey] && FIXED_OPTION_FACET_FILTER_KEYS.has(filterKey)) {
        // Fixed-vocabulary "main" filters (visitType, agesServed, pubTrans, gender,
        // newPatientStatus, boardCertification) — see FIXED_OPTION_FACET_FILTER_KEYS'
        // doc above for why these are handled separately from the raw-value sidebar
        // filters below. Rather than replacing filterDef.options wholesale with raw
        // facet text (which would lose the friendly labels this app already has for
        // these, and drop the "No preference" sentinel), narrow the existing static
        // option list down to only what the current facets response actually supports,
        // and attach each option's real result count.
        const facetOptions = facets[facetKey]?.options || [];
        const aliasMap = FIXED_OPTION_VALUE_ALIASES[filterKey] || {};
        // Build a normalized (lowercased) rawFacetValue -> count lookup once, so each
        // static option below only needs a single, case-insensitive map lookup.
        const countByRawValue = {};
        facetOptions.forEach(opt => {
          if (opt && typeof opt.value === 'string') {
            countByRawValue[opt.value.toLowerCase()] = opt.count;
          }
        });

        filter.options = (filterDef.options || []).reduce((acc, option) => {
          if (ALWAYS_VISIBLE_OPTION_VALUES.has(option.value)) {
            // Sentinel "No preference"-style option — always kept, never counted.
            acc.push(option);
            return acc;
          }

          // Find the raw facet value (if any) that corresponds to this static option,
          // via the alias map for filters whose facet text doesn't match the option
          // value directly (visitType/boardCertification/newPatientStatus), or a direct
          // case-insensitive match otherwise (gender/agesServed/pubTrans).
          const matchingRawValue = Object.keys(aliasMap).find(
            rawValue => aliasMap[rawValue] === option.value
          ) || option.value;
          const count = countByRawValue[matchingRawValue.toLowerCase()];

          // Only keep this option if the facets response actually has data for it in
          // the current result set — this is the core of "moving filter choices to
          // facets": a choice that would return zero results (e.g. "Female" when every
          // current result is male) is hidden rather than always shown.
          if (typeof count === 'number' && count > 0) {
            acc.push({ ...option, count });
          }
          return acc;
        }, []);

        console.log(`✅ Narrowed ${filter.label} to ${filter.options.length} facet-backed option(s) (of ${filterDef.options?.length || 0} possible)`);
      } else if (facetKey && facets[facetKey] && facets[facetKey].values) {
        let facetValues = facets[facetKey].values;
        
        // Special handling for handicapAccessibility - it comes as a single comma-separated string
        if (filterKey === 'handicapAccessibility' && facetValues.length === 1 && facetValues[0].includes(',')) {
          // Split the single string by comma and trim whitespace
          facetValues = facetValues[0].split(',').map(v => v.trim()).filter(v => v.length > 0);
          console.log(`🔧 Split handicapAccessibility into ${facetValues.length} individual options`);
        }

        // Special handling for specialProviderType - same shape as handicapAccessibility,
        // it can come back as a single comma-separated string instead of one entry per value.
        if (filterKey === 'specialProviderType' && facetValues.length === 1 && facetValues[0].includes(',')) {
          facetValues = facetValues[0].split(',').map(v => v.trim()).filter(v => v.length > 0);
          console.log(`🔧 Split specialProviderType into ${facetValues.length} individual options`);
        }

        // The accreditation facet has occasionally come back from the real API polluted
        // with internal system/data-quality messages (e.g. "PROVIDER NOTICE: AUTO MEMO
        // GENERATION") instead of an actual accrediting org name — these aren't real
        // accreditation options and must never be shown as filter checkboxes. Strip them
        // out here; every other value is still passed through completely unmodified/exact
        // (see the "keep the exact value from facets" comment below).
        if (filterKey === 'accreditation') {
          const beforeCount = facetValues.length;
          facetValues = facetValues.filter(value => {
            if (!value || typeof value !== 'string') return false;
            const upper = value.toUpperCase();
            return !upper.includes('PROVIDER NOTICE') && !upper.includes('AUTO MEMO');
          });
          if (facetValues.length !== beforeCount) {
            console.log(`🔧 Filtered out ${beforeCount - facetValues.length} non-accreditation system notice value(s) from accreditation facet`);
          }
        }
        
        // Convert facet values to filter options
        const isSpecialtyFilter = filterKey === 'specialties' || filter.key === 'specialty';
        // "Special provider type" (CCBHC / AI-AN) options had no tooltip explaining what
        // either abbreviation means — unlike most other filters with hasInfoIcon set at
        // the filter level, these need a PER-OPTION tooltip since "CCBHC" and "AI/AN"
        // are two distinct, unrelated values under the same filter. Mirrors the same
        // two tooltip bodies already defined for the provider details page's
        // SPECIAL_PROVIDER_TYPE_TOOLTIPS (providerDetails.jsx), keyed the same way (by
        // whether the raw facet value contains "CCBHC" or "AI/AN").
        const SPECIAL_PROVIDER_TYPE_OPTION_TOOLTIPS = [
          {
            match: (value) => typeof value === 'string' && value.toUpperCase().includes('CCBHC'),
            infoHeading: 'CCBHC provider',
            infoBody: 'These providers offer a range of services for mental health. Treatment for substance use disorders is part of these services. They treat anyone. It doesn\u2019t matter what a person\u2019s age or health condition is. Their ability to pay also doesn\u2019t affect their treatment.'
          },
          {
            match: (value) => typeof value === 'string' && value.toUpperCase().includes('AI/AN'),
            infoHeading: 'AI/AN provider',
            infoBody: 'This provider is a member of a federally recognized American Indian Tribe. Or they are an Alaska Native.'
          }
        ];
        const getSpecialProviderTypeTooltip = (value) => SPECIAL_PROVIDER_TYPE_OPTION_TOOLTIPS.find(t => t.match(value));
        const facetOptions = facetValues.map(value => ({
          // For groupAffiliation, hospitalAffiliations, accreditation, specialty, handicapAccessibility, providerRace,
          // providerEthnicity, and languagesSpoken, keep the exact value from facets without any formatting — the raw
          // label (e.g. "SIGN LANGUAGE") must be passed through as-is to the search filter/API,
          // not slugified (e.g. "sign-language"), since the backend expects the literal value.
          // For other filters, normalize by lowercasing and replacing spaces with hyphens
          // Check both filterKey and filter.key since they might differ (e.g., 'specialties' vs 'specialty')
          value: (filterKey === 'groupAffiliations' || filterKey === 'hospitalAffiliations' || filterKey === 'accreditation' || filterKey === 'specialties' || filter.key === 'specialty' || filterKey === 'handicapAccessibility' || filterKey === 'providerRace' || filterKey === 'providerEthnicity' || filterKey === 'languagesSpoken' || filterKey === 'specializedTraining' || filterKey === 'specialProviderType') ? value : value.toLowerCase().replace(/\s+/g, '-'),
          // Specialty facet values come back Title Cased (e.g. "Allergy And Immunology"),
          // which doesn't match the app's sentence-case style (only the first word
          // capitalized — see the "Show all specialties" request this accompanies). The
          // underlying `value` above is left completely untouched (still the exact,
          // unmodified facet string the search API expects) — only this display `label`
          // is sentence-cased, for the Specialty filter alone.
          label: isSpecialtyFilter ? toSentenceCase(value) : value,
          ...(filterKey === 'specialProviderType' && getSpecialProviderTypeTooltip(value) ? (() => {
            const tooltip = getSpecialProviderTypeTooltip(value);
            return { hasInfoIcon: true, infoHeading: tooltip.infoHeading, infoBody: tooltip.infoBody };
          })() : {})
        }));
        
        // For specializedTraining, merge facet options with static options
        if (filterKey === 'specializedTraining' && filterDef.options && filterDef.options.length > 0) {
          // Create a Set of existing values from static options for deduplication
          const existingValues = new Set(filterDef.options.map(opt => opt.value));
          
          // Add facet options that don't already exist in static options
          const uniqueFacetOptions = facetOptions.filter(opt => !existingValues.has(opt.value));
          
          // Combine static options with unique facet options
          filter.options = [...filterDef.options, ...uniqueFacetOptions];
          
          console.log(`✅ Combined ${filter.label}: ${filterDef.options.length} static + ${uniqueFacetOptions.length} facet options = ${filter.options.length} total`);
        } else {
          // For other filters, use facet options only
          filter.options = facetOptions;
          console.log(`✅ Populated ${filter.label} with ${filter.options.length} options from facets`);
        }
      }
    }
    
    // If no options available and this is a sidebar filter, still add it with empty options
    // (will show header only)
    filters.push(filter);
  });
  
  console.log('🔧 Built dynamic filters:', {
    totalFilters: filters.length,
    mainFilters: filters.slice(0, 4).map(f => f.key),
    sidebarFilters: filters.slice(4).map(f => f.key),
    filtersWithOptions: filters.filter(f => f.options.length > 0).map(f => `${f.key} (${f.options.length})`)
  });
  
  return filters;
};

// Legacy export for backward compatibility (uses static definitions)
export const ALL_PROVIDER_FILTERS = Object.values(STATIC_FILTER_DEFINITIONS);
